import React, { useState, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { FaEdit, FaTrash, FaPlus, FaTimes, FaEye, FaEyeSlash, FaTag } from 'react-icons/fa';
import { AppContext } from '../../App';

const BlogsManager = () => {
  const { blogs, setBlogs } = useContext(AppContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    author: 'Yagnesh Dole',
    image: '',
    tags: [],
    isPublished: true
  });
  const [tagInput, setTagInput] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Generate slug from title if not provided
    if (!formData.slug) {
      formData.slug = formData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    }
    
    if (editingBlog) {
      const updatedBlogs = blogs.map(b => 
        b.id === editingBlog.id ? { ...formData, id: b.id, createdAt: b.createdAt } : b
      );
      setBlogs(updatedBlogs);
      toast.success('Blog updated successfully!');
    } else {
      const newBlog = {
        ...formData,
        id: Date.now(),
        views: 0,
        createdAt: new Date().toISOString().split('T')[0]
      };
      setBlogs([newBlog, ...blogs]);
      toast.success('Blog added successfully!');
    }
    
    resetForm();
    setIsModalOpen(false);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this blog?')) {
      setBlogs(blogs.filter(b => b.id !== id));
      toast.success('Blog deleted successfully!');
    }
  };

  const handleToggleStatus = (id) => {
    const updatedBlogs = blogs.map(b =>
      b.id === id ? { ...b, isPublished: !b.isPublished } : b
    );
    setBlogs(updatedBlogs);
    toast.success('Blog status updated!');
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setFormData({
      title: blog.title,
      slug: blog.slug,
      excerpt: blog.excerpt,
      content: blog.content,
      author: blog.author,
      image: blog.image,
      tags: blog.tags || [],
      isPublished: blog.isPublished
    });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setEditingBlog(null);
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      author: 'Yagnesh Dole',
      image: '',
      tags: [],
      isPublished: true
    });
    setTagInput('');
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (index) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter((_, i) => i !== index)
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-primary">Manage Blogs</h2>
        <button
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          className="btn-primary flex items-center gap-2"
        >
          <FaPlus /> Write New Blog
        </button>
      </div>

      <div className="space-y-4">
        {blogs.map((blog) => (
          <motion.div
            key={blog.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-xl shadow-md p-6"
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-bold text-xl text-primary">{blog.title}</h3>
                  <span className={`text-xs px-2 py-1 rounded-full ${blog.isPublished ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                    {blog.isPublished ? 'Published' : 'Draft'}
                  </span>
                </div>
                <p className="text-gray-600 text-sm mb-2">{blog.excerpt}</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {blog.tags?.map((tag, idx) => (
                    <span key={idx} className="text-xs bg-gray-100 px-2 py-1 rounded-full">#{tag}</span>
                  ))}
                </div>
                <div className="flex gap-4 text-xs text-gray-500">
                  <span>✍️ {blog.author}</span>
                  <span>📅 {blog.createdAt}</span>
                  <span>👁️ {blog.views} views</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleToggleStatus(blog.id)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title={blog.isPublished ? 'Unpublish' : 'Publish'}
                >
                  {blog.isPublished ? <FaEye className="text-green-500" /> : <FaEyeSlash className="text-gray-400" />}
                </button>
                <button
                  onClick={() => handleEdit(blog)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FaEdit className="text-blue-500" />
                </button>
                <button
                  onClick={() => handleDelete(blog.id)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FaTrash className="text-red-500" />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
        
        {blogs.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl">
            <p className="text-gray-500">No blogs yet. Click "Write New Blog" to get started!</p>
          </div>
        )}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-gradient-to-r from-primary to-secondary text-white p-4 flex justify-between items-center">
                <h3 className="text-xl font-bold">
                  {editingBlog ? 'Edit Blog' : 'Write New Blog'}
                </h3>
                <button onClick={() => setIsModalOpen(false)} className="hover:bg-white/20 rounded-full p-2">
                  <FaTimes />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block font-semibold mb-1">Blog Title *</label>
                  <input
                    type="text"
                    required
                    className="input-field"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  />
                </div>
                
                <div>
                  <label className="block font-semibold mb-1">URL Slug</label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="auto-generated-from-title"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-') })}
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty to auto-generate from title</p>
                </div>
                
                <div>
                  <label className="block font-semibold mb-1">Excerpt / Summary *</label>
                  <textarea
                    required
                    rows="2"
                    className="input-field"
                    placeholder="Brief summary of the blog..."
                    value={formData.excerpt}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  />
                </div>
                
                <div>
                  <label className="block font-semibold mb-1">Content * (HTML supported)</label>
                  <textarea
                    required
                    rows="8"
                    className="input-field font-mono text-sm"
                    placeholder="Write your blog content here. You can use HTML tags for formatting..."
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  />
                </div>
                
                <div>
                  <label className="block font-semibold mb-1">Author</label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.author}
                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  />
                </div>
                
                <div>
                  <label className="block font-semibold mb-1">Featured Image URL</label>
                  <input
                    type="url"
                    className="input-field"
                    placeholder="https://images.unsplash.com/..."
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  />
                  <p className="text-xs text-gray-500 mt-1">Use Unsplash or any image URL</p>
                </div>
                
                <div>
                  <label className="block font-semibold mb-1">Tags</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      className="input-field flex-1"
                      placeholder="Add a tag (e.g., Investment)"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addTag()}
                    />
                    <button type="button" onClick={addTag} className="btn-primary px-4">
                      <FaTag />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.tags.map((tag, idx) => (
                      <span key={idx} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                        #{tag}
                        <button type="button" onClick={() => removeTag(idx)} className="hover:text-red-600">×</button>
                      </span>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isPublished}
                      onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="font-semibold">Publish immediately</span>
                  </label>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button type="submit" className="flex-1 btn-primary">
                    {editingBlog ? 'Update Blog' : 'Publish Blog'}
                  </button>
                  <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 btn-outline">
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BlogsManager;